import { useState } from "react";
import { Send, AlertTriangle, Loader2, MessageCircleQuestion } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const SUGGESTIONS = [
  "How does this work?",
  "How do I fix it?",
  "What tool do I need?",
  "Is this safe?",
];

export default function AskJixxeo({ scan }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [turns, setTurns] = useState([]); // [{q, a, warnings}]

  const ask = async (text) => {
    const question = (text || q).trim();
    if (!question) return;
    setBusy(true);
    try {
      const r = await api.post("/scans/ask", { scan_id: scan.id, question });
      setTurns((t) => [...t, { q: question, a: r.data.answer || "", warnings: r.data.warnings || [] }]);
      setQ("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Jixxeo AI couldn't answer");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 rounded-2xl bg-white/5 border border-white/10 overflow-hidden" data-testid="ask-jixxeo">
      <button
        data-testid="ask-jixxeo-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-3 py-2.5 flex items-center gap-2 text-left"
      >
        <div className="w-8 h-8 rounded-xl bg-cyan-400/15 border border-cyan-400/40 text-cyan-300 flex items-center justify-center">
          <MessageCircleQuestion size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-heading font-semibold leading-tight">Ask Jixxeo</p>
          <p className="text-[11px] text-slate-400 leading-tight">Reality Assistant · follow-up questions</p>
        </div>
        <span className="text-[10px] font-mono-data uppercase text-slate-500">{open ? "Hide" : "Open"}</span>
      </button>

      {open && (
        <div className="px-3 pb-3">
          <div className="space-y-2 max-h-56 overflow-y-auto no-scrollbar">
            {turns.map((t, i) => (
              <div key={i} className="space-y-1 jx-fade-up">
                <p className="text-[11px] font-mono-data uppercase text-cyan-300">You · {t.q}</p>
                <p className="text-sm text-slate-200 leading-relaxed">{t.a}</p>
                {t.warnings?.length > 0 && (
                  <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-2 flex items-start gap-2">
                    <AlertTriangle size={14} className="text-amber-300 mt-0.5 shrink-0" />
                    <ul className="text-[11px] text-amber-200 space-y-0.5">
                      {t.warnings.map((w, j) => <li key={j}>{w}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>

          {turns.length === 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  data-testid={`ask-suggestion-${s.slice(0, 8)}`}
                  onClick={() => ask(s)}
                  className="text-[11px] px-2 py-1 rounded-full bg-white/5 border border-white/10 text-slate-300 hover:border-cyan-400/40"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="mt-2 flex items-center gap-2">
            <input
              data-testid="ask-jixxeo-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Ask anything about this…"
              className="flex-1 h-10 px-3 rounded-xl bg-black/30 border border-white/10 outline-none focus:border-cyan-400/60 text-sm"
            />
            <button
              data-testid="ask-jixxeo-send"
              type="submit"
              disabled={busy || !q.trim()}
              className="h-10 w-10 rounded-xl bg-cyan-400 text-slate-950 flex items-center justify-center active:scale-95 disabled:opacity-50"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
